import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Rocket as RocketIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Message as MessageIcon,
  Analytics as AnalyticsIcon,
  Support as SupportIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Logo from '../components/Logo';
import ChatbotWidget from '../components/ChatbotWidget';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1000], [0, -200]);


  const [heroRef, heroInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [featuresRef, featuresInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [videoRef, videoInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [statsRef, statsInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [pricingRef, pricingInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [faqRef, faqInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [startedRef, startedInView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // In-view highlights for How to Get Started steps
  const inViewOpts = { threshold: 0.95, rootMargin: '-15% 0px -15% 0px', triggerOnce: false } as const;
  const [step1Ref, step1InView] = useInView(inViewOpts);
  const [step2Ref, step2InView] = useInView(inViewOpts);
  const [step3Ref, step3InView] = useInView(inViewOpts);
  const [step4Ref, step4InView] = useInView(inViewOpts);

  const startedProgress = step4InView ? 100 : step3InView ? 66 : step2InView ? 33 : step1InView ? 10 : 0;

  const features = [
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 40, color: '#10B981' }} />,
      title: 'AI-Powered Responses',
      description: 'Intelligent auto-replies that understand context and provide personalized responses.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: '#1877F2' }} />,
      title: 'Real-time Messaging',
      description: 'Instant message handling with zero delay, keeping your customers engaged.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#10A37F' }} />,
      title: 'Secure & Private',
      description: 'Enterprise-grade security ensuring your data and conversations remain protected.',
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: '#FF6B35' }} />,
      title: 'Advanced Analytics',
      description: 'Comprehensive insights into your messaging performance and customer engagement.',
    },
  ];

  const stats = [
    { number: '24/7', label: 'Availability' },
    { number: '99.9%', label: 'Uptime' },
    { number: '< 1s', label: 'Response Time' },
    { number: '∞', label: 'Messages' },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '$4.99',
      period: '/month',
      description: 'Perfect for small businesses and startups',
      features: [
        'Up to 500 messages/month',
        'Basic AI responses',
        'Email support',
        '1 social media account',
        'Basic analytics',
      ],
      popular: false,
      color: '#10B981',
      stripePlan: 'starter',
    },
    {
      name: 'Professional',
      price: '$9.99',
      period: '/month',
      description: 'Ideal for growing businesses',
      features: [
        'Up to 5,000 messages/month',
        'Advanced AI responses',
        'Priority support',
        'Up to 3 social media accounts',
        'Advanced analytics',
        'Custom auto-replies',
        'Multi-language support',
      ],
      popular: true,
      color: '#6366F1',
      stripePlan: 'pro',
    },
    {
      name: 'Enterprise',
      price: '$49.99',
      period: '/month',
      description: 'For large organizations',
      features: [
        'Unlimited messages',
        'Custom AI training',
        '24/7 phone support',
        'Unlimited social media accounts',
        'Advanced analytics & reporting',
        'Custom integrations',
        'Dedicated account manager',
        'White-label options',
      ],
      popular: false,
      color: '#E1306C',
      stripePlan: 'business',
    },
  ];

  const handleTryNow = () => {
    navigate('/login');
  };

  const handleWatchDemo = () => {
    const videoSection = document.getElementById('video-section');
    if (videoSection) {
      const headerHeight = 80; // Header yüksekliği
      const elementPosition = videoSection.offsetTop;
      const offsetPosition = elementPosition - headerHeight - 20; // 20px ekstra boşluk
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Scroll tamamlandıktan sonra videoyu 2. saniyeye al ve oynat
      setTimeout(() => {
        const video = document.querySelector('video') as HTMLVideoElement;
        if (video) {
          video.currentTime = 2; // 2. saniyeye al
          video.play(); // Oynat
        }
      }, 800); // Scroll animasyonunun tamamlanması için bekle
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (<>
    <Box sx={{ minHeight: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            py: 2,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Logo */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => { navigate('/'); scrollToTop(); }}
                >
                  <Logo height={48} />
                </Box>
              </motion.div>

              {/* Navigation */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Stack 
                  direction={{ xs: 'column', md: 'row' }} 
                  spacing={{ xs: 1, md: 3 }} 
                  alignItems="center"
                  sx={{ display: { xs: 'none', md: 'flex' } }}
                >
                  <Button
                    variant="text"
                    onClick={() => scrollToSection('features')}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Features
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => scrollToSection('pricing')}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Pricing
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => scrollToSection('footer')}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Support
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleTryNow}
                    sx={{
                      borderColor: 'rgba(16, 185, 129, 0.3)',
                      color: '#10B981',
                      fontWeight: 600,
                      px: 3,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        borderColor: '#10B981',
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                </Stack>
                
                {/* Mobile Menu Button */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Button
                    variant="outlined"
                    onClick={handleTryNow}
                    size="small"
                    sx={{
                      borderColor: 'rgba(16, 185, 129, 0.3)',
                      color: '#10B981',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#10B981',
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                </Box>
              </motion.div>
            </Box>
          </Container>
        </Box>
      </motion.div>

      {/* Animated Background */}
      <motion.div
        style={{
          position: 'fixed',
          inset: '-20vh 0 -20vh 0', // extend above and below viewport to avoid cut-off on scroll
          background: 'linear-gradient(0deg, #f1fff9, #f1fff900 30%), radial-gradient(circle at 0 0, #5fd5b6 0, #5fd5b600 30%), linear-gradient(180deg, #79dfffb3 -10%, #79dfff00 70%), radial-gradient(circle at 95% 0, #d0f224 0, #5fd5b600 20%)',
          zIndex: -1,
          y: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Floating Elements removed */}

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 16, pb: 12 }}>
        <motion.div
          ref={heroRef}
          initial={{ opacity: 0, y: 50 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={heroInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                component="img"
                src="/robot.png"
                alt="AI Chatbot"
                sx={{ 
                  width: { xs: '100%', md: 400 }, 
                  height: "auto", 
                  mb: 0,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 30px rgba(16, 185, 129, 0.3))'
                }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.8rem', md: '4.5rem' },
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #1f2937 0%, #374151 50%, #10B981 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  letterSpacing: '-0.02em',
                  lineHeight: "normal",
                }}
              >
                Social Media AI Agent
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#6b7280',
                  fontWeight: 400,
                  mb: 6,
                  maxWidth: 800,
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                }}
              >
                Deliver fast, impressive, and sales-boosting responses to your customers. Connect your social media account and manage all conversations from one place.
              </Typography>
            </motion.div>

            {/* Modern CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleTryNow}
                  sx={{
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    color: 'white',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669, #047857)',
                      boxShadow: '0 15px 40px rgba(16, 185, 129, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleWatchDemo}
                  sx={{
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                    color: '#10B981',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 3,
                    borderWidth: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#10B981',
                      background: 'rgba(16, 185, 129, 0.05)',
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Watch Demo
                </Button>
              </Box>
            </motion.div>

          </Box>
        </motion.div>

        

        {/* Video Section */}
        <motion.div
          id="video-section"
          ref={videoRef}
          initial={{ opacity: 0, y: 50 }}
          animate={videoInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ mb: 6, maxWidth: 900, mx: 'auto', position: 'relative' }}>
            {/* Ambient glow behind the video */}
            <Box
              sx={{
                position: 'absolute',
                top: -24,
                left: -24,
                right: -24,
                bottom: -24,
                zIndex: 0,
                pointerEvents: 'none',
                filter: 'blur(28px)',
                opacity: 0.7,
                background: 'radial-gradient(60% 60% at 50% 40%, rgba(16,185,129,0.35) 0%, rgba(99,102,241,0.25) 40%, rgba(225,48,108,0.18) 70%, rgba(255,255,255,0) 85%)'
              }}
            />
            <Box
              sx={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 10px 30px rgba(17, 24, 39, 0.25), 0 30px 80px rgba(16, 185, 129, 0.18)',
                zIndex: 1,
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(16, 185, 129, 0.20)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 0,
                  paddingTop: '54.25%',
                }}
              >
                <Box
                  component="video"
                  
                  src="/tutorial.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="auto"
                  poster="/poster.png"
                  
                  sx={{
                    position: 'absolute',
                    top: -1,
                    left: 0,
                    width: '101%',
                    height: 'auto',
                    border: 0,
                    objectFit: 'cover',
                    cursor: 'default',
                  }}
                />
              </Box>
            </Box>
            
            
          </Box>
        </motion.div>

        {/* FAQ Section - directly under video, no heading, lazy entrance */}
        <motion.div
          ref={faqRef}
          initial={{ opacity: 0, y: 30 }}
          animate={faqInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mt: 2, mb: 10, maxWidth: 900, mx: 'auto' }}>
            <Box>
            {/* What is Social In One? */}
            <Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  What is Social In One?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Social In One is a communication platform that brings all your customer messages into one place and uses AI to deliver smart, fast responses automatically.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* How does Social In One work? */}
            <Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  How does Social In One work?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  It connects all your platforms in one place. AI analyzes messages based on your custom prompts and generates smart, personalized responses. You control the tone and style — fully customizable and AI-powered.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* What are the benefits? */}
            <Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  What are the benefits?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Turn conversations into sales with instant, personalized responses. Happy customers buy more and return faster. Save time, boost revenue, and stay ahead of competitors while AI works 24/7 for you.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* What happens if I don't use it? */}
            <Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  What if I don't use Social In One?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  You'll respond slowly, miss opportunities, and lose customers to faster competitors. Speed equals trust and sales — without AI, you fall behind.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Original FAQs - keeping them below */}
            <Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  Which social media accounts can I connect?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  You can connect your Instagram and Facebook pages to manage conversations from a single panel. More platforms may be added soon.
                </Typography>
              </AccordionDetails>
            </Accordion>

<Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  Can I customize prompts?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Yes. You can create your own prompts, toggle them on/off, and test in a realistic scenario with Prompt Tester. You can also generate prompts by uploading an image.
                </Typography>
              </AccordionDetails>
            </Accordion>

<Accordion 
              disableGutters 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 3, 
                mb: 2, 
                border: '1px solid rgba(16, 185, 129, 0.1)',
                boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                },
                '&.Mui-expanded': {
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(99, 102, 241, 0.05))',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  boxShadow: '0 8px 30px rgba(16, 185, 129, 0.2)',
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#10B981' }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                  }
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '1rem' }}>
                  How long does setup take?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                <Typography variant="body2" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                  Connecting your account typically takes about 30 seconds. Once connected, your messages appear in one panel and you can enable automated replies.
                </Typography>
              </AccordionDetails>
            </Accordion>
            </Box>
            {/* Try Now Button */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={handleTryNow}
                sx={{
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                  color: '#6366F1',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  '&:hover': {
                    borderColor: '#6366F1',
                    background: 'rgba(99, 102, 241, 0.05)',
                  },
                }}
                endIcon={<ArrowForwardIcon />}
              >
                Try Now
              </Button>
            </Box>
          </Box>
        </motion.div>

        {/* Features Section */}
        <motion.div
          ref={featuresRef}
          id="features"
          initial={{ opacity: 0, y: 50 }}
          animate={featuresInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 6,
                color: 'text.primary',
              }}
            >
              Why Choose Our AI Agent?
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4 }}>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: 'text.primary',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Box>
          </Box>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          ref={statsRef}
          initial={{ opacity: 0, y: 50 }}
          animate={statsInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              p: 4,
              mb: 8,
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4 }}>
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={statsInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: '#10B981',
                        mb: 1,
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>
        </motion.div>

        {/* How to Get Started - Vertical Steps (moved above Pricing) */}
        <motion.div
          ref={startedRef}
          initial={{ opacity: 0, y: 50 }}
          animate={startedInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
        <Box sx={{ mt: 10, mb: 10 }}>
          <Box
            sx={{
              background: 'linear-gradient(180deg, rgba(16,185,129,0.06) 0%, rgba(16,185,129,0.03) 100%)',
              border: '1px solid rgba(16,185,129,0.15)',
              borderRadius: 3,
              px: { xs: 2, md: 6 },
              py: { xs: 6, md: 8 },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="overline" sx={{ color: '#059669', letterSpacing: 1, fontWeight: 700 }}>
                Simple 4‑Step Process
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
                How to <span style={{ color: '#10B981' }}>Get Started</span>
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
                Getting started takes just minutes. Set up Social In One and start AI‑powered messaging right away.
              </Typography>
            </Box>

              {/* Desktop: Vertical timeline */}
              <Box sx={{ maxWidth: 900, mx: 'auto', position: 'relative', pl: { xs: 0, md: 7 }, display: { xs: 'none', md: 'block' } }}>
                {/* Vertical guide line (spine) with progress fill */}
                <Box sx={{ position: 'absolute', left: 24, top: 0, bottom: 0, width: 2, background: 'rgba(16,185,129,0.15)', borderRadius: 1 }} />
                <Box sx={{ position: 'absolute', left: 24, top: 0, width: 2, height: `${startedProgress}%`, background: '#10B981', boxShadow: '0 0 8px rgba(16,185,129,0.4)', borderRadius: 1, transition: 'height 600ms ease' }} />

                {/* Step 1 */}
                <Box ref={step1Ref} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pb: 5, position: 'relative' }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#FFFFFF',
                    border: '2px solid rgba(16,185,129,0.75)',
                    boxShadow: '0 0 0 6px rgba(16,185,129,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800,
                  }}>1</Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Connect Your Accounts
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Link all your social or messaging platforms to Social In One in a few clicks. <strong>Quick 5‑minute setup.</strong>
                    </Typography>
                  </Box>
                </Box>

                {/* Step 2 */}
                <Box ref={step2Ref} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pb: 5, position: 'relative' }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', background: '#FFFFFF', border: '2px solid rgba(16,185,129,0.75)', boxShadow: '0 0 0 6px rgba(16,185,129,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800
                  }}>2</Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Add Your Prompt
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Write a short prompt that defines how the AI should speak. Set your brand’s tone and style.
                    </Typography>
                  </Box>
                </Box>

                {/* Step 3 */}
                <Box ref={step3Ref} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', pb: 5, position: 'relative' }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', background: '#FFFFFF', border: '2px solid rgba(16,185,129,0.75)', boxShadow: '0 0 0 6px rgba(16,185,129,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800
                  }}>3</Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Test Your Prompt
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Try the AI’s responses and refine when needed. Ensure the tone and meaning are on point.
                    </Typography>
                  </Box>
                </Box>

                {/* Step 4 */}
                <Box ref={step4Ref} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', position: 'relative' }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', background: '#FFFFFF', border: '2px solid rgba(16,185,129,0.75)', boxShadow: '0 0 0 6px rgba(16,185,129,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800
                  }}>4</Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Activate AI in Messages
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      When everything looks good, enable AI in the Messages page. Start replying instantly and intelligently.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Mobile: Card-based layout */}
              <Box sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: '1fr', gap: 2 }}>
                {/* Step 1 */}
                <Box sx={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 2, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: '#FFFFFF', border: '2px solid #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800, flexShrink: 0 }}>1</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      Connect Your Accounts
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.45 }}>
                      Link all your social or messaging platforms in a few clicks. <strong>Quick 5‑minute setup.</strong>
                    </Typography>
                  </Box>
                </Box>

                {/* Step 2 */}
                <Box sx={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 2, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: '#FFFFFF', border: '2px solid #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800, flexShrink: 0 }}>2</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      Add Your Prompt
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.45 }}>
                      Write a short prompt that defines how the AI should speak. Set your brand’s tone and style.
                    </Typography>
                  </Box>
                </Box>

                {/* Step 3 */}
                <Box sx={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 2, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: '#FFFFFF', border: '2px solid #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800, flexShrink: 0 }}>3</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      Test Your Prompt
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.45 }}>
                      Try the AI’s responses and refine when needed. Ensure the tone and meaning are on point.
                    </Typography>
                  </Box>
                </Box>

                {/* Step 4 */}
                <Box sx={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 2, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: '#FFFFFF', border: '2px solid #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontWeight: 800, flexShrink: 0 }}>4</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                      Activate AI in Messages
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.45 }}>
                      When everything looks good, enable AI in the Messages page. Start replying instantly and intelligently.
                    </Typography>
                  </Box>
                </Box>
              </Box>
          </Box>
        </Box>
        </motion.div>

        {/* Pricing Section */}
        <motion.div
          ref={pricingRef}
          id="pricing"
          initial={{ opacity: 0, y: 50 }}
          animate={pricingInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 6,
                fontWeight: 300,
              }}
            >
              Choose the plan that fits your business needs
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
              {pricingPlans.map((plan, index) => (
                <Box key={index} sx={{ overflow: 'visible' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={pricingInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        background: plan.popular ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(99, 102, 241, 0.1))' : 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: plan.popular ? `2px solid ${plan.color}` : '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 3,
                        boxShadow: plan.popular ? '0 20px 40px rgba(99, 102, 241, 0.2)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'visible',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: plan.popular ? '0 25px 50px rgba(99, 102, 241, 0.3)' : '0 20px 40px rgba(0, 0, 0, 0.15)',
                        },
                      }}
                    >
                      {plan.popular && (
                        <Chip
                          label="Most Popular"
                          sx={{
                            position: 'absolute',
                            top: -17,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: plan.color,
                            color: 'white',
                            fontWeight: 600,
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        />
                      )}
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            mb: 1,
                            color: 'text.primary',
                          }}
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 800,
                            color: plan.color,
                            mb: 0.5,
                          }}
                        >
                          {plan.price}
                          <Typography
                            component="span"
                            variant="body1"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 400,
                            }}
                          >
                            {plan.period}
                          </Typography>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            mb: 3,
                            fontWeight: 300,
                          }}
                        >
                          {plan.description}
                        </Typography>
                        <List sx={{ textAlign: 'left', mb: 3 }}>
                          {plan.features.map((feature, featureIndex) => (
                            <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon sx={{ color: plan.color, fontSize: 20 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={feature}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  sx: { color: 'text.primary', fontWeight: 400 },
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.15))',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.5,
                            mb: 2,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#059669',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          >
                            🎉 7 Days Free • Then {plan.price}/mo
                          </Typography>
                        </Box>
                        <Button
                          variant={plan.popular ? 'contained' : 'outlined'}
                          fullWidth
                          onClick={() => {
                            // Redirect to signup/login with plan parameter
                            navigate(`/login?plan=${(plan as any).stripePlan}&redirect=checkout&source=landing`);
                          }}
                          sx={{
                            background: plan.popular ? plan.color : 'transparent',
                            color: plan.popular ? 'white' : plan.color,
                            borderColor: plan.color,
                            fontWeight: 600,
                            py: 1.5,
                            '&:hover': {
                              background: plan.popular ? `${plan.color}dd` : `${plan.color}10`,
                            },
                          }}
                        >
                          Get Started
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Box
            sx={{
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
              borderRadius: 3,
              p: 6,
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
              }}
            >
              Ready to Transform Your Social Media?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                mb: 4,
                fontWeight: 300,
              }}
            >
              Join thousands of businesses already using our AI agent to boost engagement and grow their audience.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login?redirect=checkout')}
              sx={{
                background: 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: '0 15px 40px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669, #047857)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 20px 50px rgba(16, 185, 129, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              endIcon={<ArrowForwardIcon />}
            >
              Get Started Now
            </Button>
          </Box>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box
        id="footer"
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
          color: 'white',
          mt: 12,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, 
            gap: 4,
            justifyContent: { xs: 'center', md: 'stretch' }
          }}>
            {/* Company Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Logo height={32} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.6,
                    mb: 3,
                    maxWidth: { xs: '100%', md: '64%' },
                  }}
                >
                  Transform your social media presence with AI-powered automation. 
                  Engage with your audience 24/7 and grow your brand effortlessly.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }}>
                  <IconButton
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#1877F2' },
                    }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/socialinone/"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#E1306C' },
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.tiktok.com/@socialinone"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#00f2ea' },
                    }}
                  >
                    <Box
                      component="svg"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                      sx={{ width: 24, height: 24, fill: 'currentColor' }}
                    >
                      <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z"/>
                    </Box>
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.youtube.com/@SocialinOne"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': { color: '#FF0000' },
                    }}
                  >
                    <YouTubeIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Box>


            {/* Support */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Support
              </Typography>
              <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/faq')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  FAQ
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/contact')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  component={Link}
                  to="/help-center"
                  variant="text"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Help Center
                </Button>
              </Stack>
            </Box>



            {/* Contact Info */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    info@socialinone.ai
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <LocationIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Almere, Netherlands
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'center' },
              gap: 2,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              © 2025 NowUp. All rights reserved.
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 3 },
                ml: { xs: 0, md: 0 },
              }}
            >
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/terms-of-use')}
              >
                Terms of Service
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/cookie-policy')}
              >
                Cookie Policy
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
    <ChatbotWidget />
  </>);
};

export default Landing; 